/**
 * PlPopover - Custom element for accessible popover components
 * Handles positioning, keyboard navigation, and focus management
 */
export class PlPopover extends HTMLElement {
  // Private fields
  _trigger = null
  _content = null
  _hoverTimeout = null
  _isInitialized = false
  _closeOnOutsideClick = true
  _closeOnEscape = true
  _closeDelay = 50

  constructor() {
    super()
    this.handleTriggerClick = this.handleTriggerClick.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.handleBasecoatPopover = this.handleBasecoatPopover.bind(this)
  }

  connectedCallback() {
    this.#init()
  }

  disconnectedCallback() {
    this.#cleanup()
  }

  #init() {
    // Find trigger and content
    this._trigger = this.querySelector(":scope > button, :scope > [role='button']")
    this._content = this.querySelector(":scope > [data-pl-popover]")

    if (!this._trigger || !this._content) {
      const missing = []
      if (!this._trigger) missing.push("trigger")
      if (!this._content) missing.push("content")
      console.error(`pl-popover: Missing element(s): ${missing.join(", ")}`, this)
      return
    }

    // Get configuration from data attributes
    this._closeOnOutsideClick = this.dataset.closeOnOutsideClick !== "false"
    this._closeOnEscape = this.dataset.closeOnEscape !== "false"
    this._closeDelay = this.dataset.closeDelay ? parseInt(this.dataset.closeDelay, 10) : 50
    console.log("this._closeDelay", this._closeDelay)

    // Set up ARIA attributes
    if (!this._trigger.id) {
      this._trigger.id = `popover-trigger-${Math.random().toString(36).substr(2, 9)}`
    }
    if (!this._content.id) {
      this._content.id = `popover-content-${Math.random().toString(36).substr(2, 9)}`
    }

    this._trigger.setAttribute("aria-haspopup", "true")
    this._trigger.setAttribute("aria-controls", this._content.id)
    // Respect initial aria-hidden state from HTML
    const initialAriaHidden = this._content.getAttribute("aria-hidden")
    const isInitiallyOpen = initialAriaHidden === "false"

    this._trigger.setAttribute("aria-expanded", isInitiallyOpen ? "true" : "false")
    this._content.setAttribute("aria-hidden", isInitiallyOpen ? "false" : "true")
    this._content.setAttribute("aria-labelledby", this._trigger.id)

    // Set up positioning
    this.#setPosition()

    // Add event listeners
    this.#attachListeners()

    // Mark as initialized
    this._isInitialized = true
    this.dataset.popoverInitialized = "true"
    this.dispatchEvent(new CustomEvent("pl-popover:initialized", { bubbles: false }))
  }

  #setPosition() {
    const side = this.dataset.side || "bottom"
    const align = this.dataset.align || "start"

    this._content.dataset.side = side
    this._content.dataset.align = align
  }

  #attachListeners() {
    this._trigger.addEventListener("click", this.handleTriggerClick)
    this.addEventListener("keydown", this.handleKeydown)

    // Add hover support
    const triggerType = this.dataset.triggerType || "click"
    if (triggerType === "hover" || triggerType === "both") {
      this._hoverTimeout = null

      // Open on trigger hover
      this._trigger.addEventListener("mouseenter", () => {
        this._clearHoverTimeout()
        // Close sibling dropdowns in the same menu
        this.#closeSiblingPopovers()
        this.open()
      })

      // Close with delay when leaving trigger
      this._trigger.addEventListener("mouseleave", () => {
        this._scheduleClose()
      })

      // Keep open when entering content
      this._content.addEventListener("mouseenter", () => {
        this._clearHoverTimeout()
      })

      // Close with delay when leaving content
      this._content.addEventListener("mouseleave", () => {
        this._scheduleClose()
      })
    }

    if (this._closeOnOutsideClick) {
      document.addEventListener("click", this.handleOutsideClick)
    }

    // Listen for other popovers opening (close this one)
    document.addEventListener("pl-popover:open", this.handleBasecoatPopover)
  }

  #closeSiblingPopovers() {
    // Find the parent menu container
    const parentMenu = this.closest('[role="menu"]')
    if (!parentMenu) return

    // Find all sibling popovers/dropdowns in the same menu
    const siblingPopovers = parentMenu.querySelectorAll("pl-popover, pl-dropdown")

    siblingPopovers.forEach((popover) => {
      // Don't close:
      // 1. This popover itself
      // 2. Popovers nested inside this one (children)
      // 3. Popovers that contain this one (parents)

      if (popover === this) return

      const isChild = this.contains(popover)
      const isParent = popover.contains(this)

      // Only close sibling popovers (same level in the menu)
      if (!isChild && !isParent) {
        const isOpen = popover._trigger?.getAttribute("aria-expanded") === "true"
        if (isOpen) {
          popover.close(false)
        }
      }
    })
  }

  _scheduleClose() {
    this._clearHoverTimeout()
    this._hoverTimeout = setTimeout(() => {
      this.close(false)
    }, this._closeDelay) // 10ms delay to allow mouse to cross gap
  }

  _clearHoverTimeout() {
    if (this._hoverTimeout) {
      clearTimeout(this._hoverTimeout)
      this._hoverTimeout = null
    }
  }

  #cleanup() {
    this._clearHoverTimeout()
    if (this._trigger) {
      this._trigger.removeEventListener("click", this.handleTriggerClick)
    }
    this.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("click", this.handleOutsideClick)
    document.removeEventListener("pl-popover:open", this.handleBasecoatPopover)
  }

  handleTriggerClick(event) {
    event.preventDefault()
    event.stopPropagation()

    const isExpanded = this._trigger.getAttribute("aria-expanded") === "true"
    if (isExpanded) {
      this.close()
    } else {
      this.open()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this._closeOnEscape) {
      const isExpanded = this._trigger.getAttribute("aria-expanded") === "true"
      if (isExpanded) {
        event.preventDefault()
        this.close()
      }
    }
  }

  handleOutsideClick(event) {
    const isExpanded = this._trigger.getAttribute("aria-expanded") === "true"
    if (isExpanded && !this.contains(event.target)) {
      // Check if click is inside a nested popover
      const clickedPopover = event.target.closest("pl-popover, pl-dropdown")
      const isNestedPopover =
        clickedPopover && clickedPopover !== this && this.contains(clickedPopover)

      // Don't close if click is in a nested popover
      if (!isNestedPopover) {
        this.close(false)
      }
    }
  }

  handleBasecoatPopover(event) {
    const newPopover = event.detail.source

    // Don't close if:
    // 1. It's the same popover
    // 2. The new popover is nested inside this one (child)
    // 3. This popover is nested inside the new one (parent)

    if (newPopover === this) return

    const isChildPopover = this.contains(newPopover)
    const isParentPopover = newPopover.contains(this)

    // Only close if it's a sibling popover (not parent/child)
    if (!isChildPopover && !isParentPopover) {
      this.close(false)
    }
  }

  /**
   * Open the popover
   */
  open() {
    if (!this._isInitialized) return

    // Notify other popovers to close (but not nested ones)
    document.dispatchEvent(
      new CustomEvent("pl-popover:open", {
        detail: { source: this },
        bubbles: true,
      })
    )

    // Update ARIA
    this._trigger.setAttribute("aria-expanded", "true")
    this._content.setAttribute("aria-hidden", "false")

    // Handle autofocus
    const elementToFocus = this._content.querySelector("[autofocus]")
    if (elementToFocus) {
      // Wait for transition to complete
      this._content.addEventListener(
        "transitionend",
        () => {
          elementToFocus.focus()
        },
        { once: true }
      )
    }

    // Dispatch open event
    this.dispatchEvent(
      new CustomEvent("pl-popover:opened", {
        bubbles: true,
        detail: { trigger: this._trigger, content: this._content },
      })
    )
  }

  /**
   * Close the popover
   * @param {boolean} focusOnTrigger - Whether to return focus to trigger
   */
  close(focusOnTrigger = true) {
    if (!this._isInitialized) return

    const isExpanded = this._trigger.getAttribute("aria-expanded") === "true"
    if (!isExpanded) return

    // Update ARIA
    this._trigger.setAttribute("aria-expanded", "false")
    this._content.setAttribute("aria-hidden", "true")

    // Return focus to trigger
    if (focusOnTrigger) {
      this._trigger.focus()
    }

    // Dispatch close event
    this.dispatchEvent(
      new CustomEvent("pl-popover:closed", {
        bubbles: true,
        detail: { trigger: this._trigger, content: this._content },
      })
    )
  }

  /**
   * Toggle the popover
   */
  toggle() {
    const isExpanded = this._trigger.getAttribute("aria-expanded") === "true"
    if (isExpanded) {
      this.close()
    } else {
      this.open()
    }
  }

  /**
   * Get the current state
   */
  get isOpen() {
    return this._trigger?.getAttribute("aria-expanded") === "true"
  }
}

// Register the custom element
if (!customElements.get("pl-popover")) {
  customElements.define("pl-popover", PlPopover)
}
